import type { T_Maybe } from '@tryiris-ai/js-types';
import { isEmbeddedShadowDeviceId } from '#~/device/embedded-shadow-id.js';

/**
 * embedded link recovery: decide whether a bucketed cloud track keyed by `deviceId` should be
 * routed to (rendered for) `agentId`.
 *
 * The render-side filters in `videoForAgent` / `audioForAgent` (livekit.ts) used
 * to gate purely on the device→agent map: `toHexFromId(#deviceIdLookup(id)) ===
 * agentId`. For a just-linked EMBEDDED camera the camera publishes its cloud
 * track (participant identity = the device id) BEFORE state-sync delivers
 * `control.device[deviceId]`, so `#deviceIdLookup` (→ `locateProxy`) returns
 * undefined → `toHexFromId(undefined) === agentId` is `undefined === <hex>` =
 * false → the (already-subscribed, already-bucketed) track is dropped from the
 * rendered map and the source sits on "Connecting…" until a manual reload. That
 * is the read-side gate the subscribe-side fix could not reach (a dead-ended
 * write).
 *
 * Pure + exported for unit-testability (mirrors auth/room-selection.ts and
 * routes/boot-overlay/boot-stall.ts). Per rule: think-in-systems (ai.knowledge/global).
 */

/**
 * @param deviceId         bucket key (the LiveKit participant identity / device id).
 * @param agentId          hex agent id the filter is collecting tracks for.
 * @param embedded         whether the target agent is an embedded camera agent.
 * @param resolvedAgentHex `toHexFromId(#deviceIdLookup(deviceId))` — the agent the
 *                         device→agent map resolves this device to, or undefined
 *                         while state-sync has not delivered `control.device` yet.
 */
export const trackBelongsToAgent = (
  deviceId: string,
  agentId: string,
  embedded: boolean,
  resolvedAgentHex: T_Maybe<string>
): boolean => {
  // Normal path: the map resolved — strict, exact match (unchanged behavior).
  if (resolvedAgentHex !== undefined) {
    return resolvedAgentHex === agentId;
  }

  // embedded link recovery fallback: map not yet resolved (state-sync lag). Route by the
  // deterministic embedded shadow-id relationship instead of dropping the track.
  // Strictly scoped to embedded agents + the '0001' shadow pattern so a
  // non-embedded device with an unresolved map stays excluded (never loosens the
  // strict mapping, and never matches undefined===undefined).
  return embedded && isEmbeddedShadowDeviceId(deviceId, agentId);
};
